import { type FC, useMemo, useRef } from 'react';

import { cx } from '@emotion/css';
import { useSelector } from 'react-redux';

import { useParamSelector } from 'hooks/use-param-selector';
import { getIsAIAgentAvailableByName } from 'store/entities/ai-agents/selectors';
import { getIsOnlyWelcomeMessage, getCopilotGroupedEvents } from 'store/entities/copilot/computed';
import { getIsCopilotError } from 'store/entities/copilot/selectors';
import {
  getIsCommandSuggestionsOpened,
  getIsCopilotModalExpanded,
  getCopilotIsLoading,
} from 'store/views/copilot/selectors';

import { expandChatContentAnimation } from './animations';
import { COPILOT_CHAT_FEED_ID } from './constants';
import { CopilotChatFeedSkeleton } from './CopilotChatFeedSkeleton';
import { CopilotFullscreenEmptyState } from './CopilotFullscreenEmptyState';
import { CopilotFullscreenNoAIAgentsScreen } from './CopilotFullScreenNoAIAgentsScreen';
import { CopilotEventsGroup } from './events/CopilotEventsGroup';
import { CopilotStreamMessage } from './events/CopilotStreamMessage';
import { createGroupedEvents } from './helpers';
import { useScroll } from './hooks/use-scroll';
import { PopoverSuggestions } from './suggestions/PopoverSuggestions';

import * as styles from './styles';

export const CopilotChatFeedContent: FC = () => {
  const copilotChatFeedRef = useRef<HTMLDivElement>(null);
  const isExpanded = useSelector(getIsCopilotModalExpanded);

  const groupedEvents = useSelector(getCopilotGroupedEvents);
  const isCopilotLoading = useSelector(getCopilotIsLoading);
  const isCopilotError = useSelector(getIsCopilotError);
  const isOnlyWelcomeMessage = useSelector(getIsOnlyWelcomeMessage);
  const isCommandSuggestionsOpened = useSelector(getIsCommandSuggestionsOpened);
  const hasCopilotAIAgent = useParamSelector(getIsAIAgentAvailableByName, 'one');

  const eventGroups = useMemo(
    () =>
      createGroupedEvents({
        groupedEvents,
        isCopilotError,
      }),
    [groupedEvents, isCopilotError],
  );

  // eslint-disable-next-line react-compiler/react-compiler
  const { handleScroll } = useScroll({
    scrollRef: copilotChatFeedRef,
    groupedEvents,
    isLoading: isCopilotLoading,
  });

  if (groupedEvents.flat().length === 0 && !isCopilotLoading) {
    return <CopilotChatFeedSkeleton />;
  }

  if (isOnlyWelcomeMessage && isExpanded && !isCopilotLoading) {
    return <CopilotFullscreenEmptyState />;
  }

  if (!hasCopilotAIAgent && isExpanded) {
    return <CopilotFullscreenNoAIAgentsScreen />;
  }

  return (
    <div
      id={COPILOT_CHAT_FEED_ID}
      onScroll={handleScroll}
      ref={copilotChatFeedRef}
      className={cx(styles.scroller(isExpanded, isCommandSuggestionsOpened), {
        [expandChatContentAnimation]: isExpanded,
      })}
    >
      <div className={styles.chatMessagesWrapper(isExpanded)}>
        {eventGroups.map((eventGroup, index) => (
          <CopilotEventsGroup key={index} eventGroup={eventGroup} />
        ))}
        {!isExpanded && <PopoverSuggestions />}
        <CopilotStreamMessage />
      </div>
    </div>
  );
};
